import { type KeyboardEvent, useEffect, useLayoutEffect, useRef } from 'react'

import type { RootState } from '@/store/store'
import { selectActiveContentViewerSession } from '@/content-viewer/store/selectors'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { kChatPanelTabIds } from '../chat-panel-tab-ids'
import { selectChatPanelOpen } from '../store/selectors'
import { ChatPanelTabType, DraftWorkspaceActions } from '../store/slice'
import { useChatPanelTabs } from './useChatPanelTabs'
import { useDraftWorkspace } from './useDraftWorkspace'

/**
 * Brings a source that has just opened into view in the chat panel: selects its tab, reopens the panel if it was
 * collapsed, and, unless it was opened with a pointer, moves focus to the tab once it is on screen.
 */
export function useSelectSourceTabOnOpen() {
  const dispatch = useAppDispatch()
  const { draftArtifactVersionId } = useDraftWorkspace()
  const { hasSourceTab, sourceVisible } = useChatPanelTabs()
  const chatPanelOpen = useAppSelector((state: RootState) => (draftArtifactVersionId ? selectChatPanelOpen(state, { draftArtifactVersionId }) : false))
  const viewerSource = useAppSelector(selectActiveContentViewerSession)
  const shownSource = useRef(viewerSource)
  const focusSourceOnReveal = useRef(false)
  const openedWithoutToggleRef = useRef(false)
  const lastInputWasPointer = useRef(false)

  // Captured so the reader's latest input is recorded before any handler opens a source.
  useEffect(() => {
    const handlePointerDown = () => {
      lastInputWasPointer.current = true
    }
    const handleKeyDown = () => {
      lastInputWasPointer.current = false
    }
    document.addEventListener('pointerdown', handlePointerDown, true)
    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [])

  /**
   * While a draft is open, a source opening switches the chat panel to its source tab, wherever it was opened
   * from. Opening means the content viewer's session changed, so a source already open when the panel gains
   * its tab (entering a draft, or widening past the small screen breakpoint) leaves the selected tab alone.
   */
  useLayoutEffect(() => {
    const sourceJustOpened = viewerSource !== null && viewerSource !== shownSource.current
    shownSource.current = viewerSource
    if (!sourceJustOpened || !hasSourceTab || !draftArtifactVersionId) return

    // A source opened with a pointer leaves focus where the reader clicked.
    focusSourceOnReveal.current = !lastInputWasPointer.current
    dispatch(DraftWorkspaceActions.setChatPanelTab({ draftArtifactVersionId, tab: ChatPanelTabType.Source }))
    if (chatPanelOpen) return

    // Reopening the panel here is not the reader reaching for the toggle, so it keeps their focus.
    openedWithoutToggleRef.current = true
    dispatch(DraftWorkspaceActions.setChatPanelOpen({ draftArtifactVersionId, open: true }))
  }, [chatPanelOpen, dispatch, draftArtifactVersionId, hasSourceTab, viewerSource])

  // Found by id rather than a ref, since the panel header renders the tab bar several levels away.
  const focusSourceTab = () => document.getElementById(kChatPanelTabIds[ChatPanelTabType.Source].tab)?.focus()

  /**
   * A source is reached once its tab is on screen rather than when it opens, since a collapsed or unselected
   * panel cannot take focus.
   */
  useLayoutEffect(() => {
    if (!sourceVisible || !focusSourceOnReveal.current) return
    focusSourceOnReveal.current = false
    focusSourceTab()
    // viewerSource is a dependency so that replacing a source re-runs this, even though its tab is already selected.
  }, [sourceVisible, viewerSource])

  /**
   * Opens the panel on a source that is already open, moving focus to its tab. Unlike a source opening, a pointer
   * leaves focus nowhere to stay: the control that opens a source already open is the collapsed panel's chip,
   * which goes away with the panel it reopens.
   */
  const openSourceTab = () => {
    if (!draftArtifactVersionId) return
    focusSourceOnReveal.current = true
    openedWithoutToggleRef.current = !chatPanelOpen
    dispatch(DraftWorkspaceActions.setChatPanelTab({ draftArtifactVersionId, tab: ChatPanelTabType.Source }))
    dispatch(DraftWorkspaceActions.setChatPanelOpen({ draftArtifactVersionId, open: true }))
  }

  // Escape leaves the source for its tab rather than closing it, so the reader can come back to it.
  const handleSourcePanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return
    focusSourceTab()
  }

  return { openedWithoutToggleRef, handleSourcePanelKeyDown, openSourceTab }
}
